<template>
    <div class="mb-8 md:mb-12">
        <!-- Search Bar -->
        <div class="mb-4 md:mb-6">
            <div
                class="flex w-full h-[52px] md:h-auto p-1 md:p-2 flex-col items-start justify-center gap-2.5 rounded-lg bg-white dark:bg-slate-800 shadow-md"
            >
                <div class="relative w-full">
                    <div
                        class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
                    >
                        <Icon
                            icon="search"
                            class="h-5 w-5 text-slate-800 dark:text-slate-100"
                        />
                    </div>
                    <input
                        :value="searchQuery"
                        @input="$emit('update:searchQuery', $event.target.value)"
                        @keyup.enter="updateFilters()"
                        type="text"
                        class="block w-full pl-12 pr-12 py-2 md:py-2.5 border border-transparent rounded-lg text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 bg-transparent placeholder-slate-500 focus:outline-none focus:placeholder-slate-400 focus:ring-0 focus:border-slate-800"
                        placeholder="Search by program"
                    />
                    <button
                        v-if="searchQuery && searchQuery.trim() !== ''"
                        type="button"
                        class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100"
                        @click="clearSearch()"
                        aria-label="Clear search"
                    >
                        <Icon icon="close" class="w-4 h-4" />
                    </button>
                    <label
                        class="sr-only text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                    >
                        Search by program
                    </label>
                </div>
            </div>
        </div>

        <!-- Mobile Filters button (hidden on Favorites) -->
        <div class="md:hidden mb-6" v-if="!filters.favorites">
            <button
                type="button"
                @click="$emit('open-mobile-filters')"
                class="flex items-center gap-2 px-4 py-2 h-9 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
            >
                <Icon icon="filters" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                    Filters
                </span>
                <Transition name="fade">
                    <span
                        v-if="activeFiltersCount > 0"
                        class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                    >
                        {{ activeFiltersCount }}
                    </span>
                </Transition>
            </button>
        </div>

        <!-- Mobile Favorites toolbar: icon-only Sort and Group buttons -->
        <div
            class="md:hidden mb-6 flex items-center justify-start gap-2"
            v-else
        >
            <button
                type="button"
                class="flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
                @click="openSortOverlay()"
                aria-label="Open sort options"
            >
                <Icon icon="sort" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
            </button>

            <button
                type="button"
                class="flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700"
                @click="openGroupOverlay()"
                aria-label="Open group options"
            >
                <Icon icon="groups" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
            </button>
        </div>

        <!-- Mobile Overlays: Sort -->
        <transition name="fade">
            <div
                v-if="showSortOverlay"
                class="md:hidden fixed inset-0 z-50"
            >
                <div
                    class="absolute inset-0 bg-black/40"
                    @click="closeSortOverlay()"
                ></div>
                <div
                    class="absolute left-0 right-0 bottom-0 rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-4"
                >
                    <div class="flex items-center justify-between mb-2">
                        <h3 class="text-slate-800 dark:text-slate-100 text-lg font-semibold">
                            Sort by
                        </h3>
                        <button
                            class="p-2 -mr-2"
                            @click="closeSortOverlay()"
                            aria-label="Close sort"
                        >
                            <Icon
                                icon="close"
                                class="w-5 h-5 text-slate-500 dark:text-slate-400"
                            />
                        </button>
                    </div>
                    <label
                        v-for="option in sortOptions"
                        :key="option.id"
                        class="flex items-center gap-3 py-3 cursor-pointer"
                    >
                        <input
                            type="radio"
                            class="text-10x-orange"
                            :value="option"
                            :checked="tempSelectedSort.id === option.id"
                            @change="$emit('update:tempSelectedSort', option)"
                        />
                        <span class="text-slate-800 dark:text-slate-100 text-sm">
                            {{ option.name }}
                        </span>
                    </label>
                    <button
                        type="button"
                        class="mt-4 w-full h-11 rounded-lg bg-slate-800 text-white text-sm font-medium"
                        @click="applySortOverlay()"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </transition>

        <!-- Mobile Overlays: Group -->
        <transition name="fade">
            <div
                v-if="showGroupOverlay"
                class="md:hidden fixed inset-0 z-50"
            >
                <div
                    class="absolute inset-0 bg-black/40"
                    @click="closeGroupOverlay()"
                ></div>
                <div
                    class="absolute left-0 right-0 bottom-0 rounded-2xl bg-white dark:bg-slate-800 shadow-xl p-4"
                >
                    <div class="flex items-center justify-between mb-2">
                        <h3 class="text-slate-800 dark:text-slate-100 text-lg font-semibold">
                            Group by
                        </h3>
                        <button
                            class="p-2 -mr-2"
                            @click="closeGroupOverlay()"
                            aria-label="Close group"
                        >
                            <Icon
                                icon="close"
                                class="w-5 h-5 text-slate-500 dark:text-slate-400"
                            />
                        </button>
                    </div>
                    <label
                        v-for="option in groupOptions"
                        :key="option.id"
                        class="flex items-center gap-3 py-3 cursor-pointer"
                    >
                        <input
                            type="radio"
                            class="text-10x-orange"
                            :value="option"
                            :checked="tempSelectedGroup.id === option.id"
                            @change="$emit('update:tempSelectedGroup', option)"
                        />
                        <span class="text-slate-800 dark:text-slate-100 text-sm">
                            {{ option.name }}
                        </span>
                    </label>
                    <button
                        type="button"
                        class="mt-4 w-full h-11 rounded-lg bg-slate-800 text-white text-sm font-medium"
                        @click="applyGroupOverlay()"
                    >
                        Apply
                    </button>
                </div>
            </div>
        </transition>

        <!-- Desktop Filters -->
        <div class="hidden md:flex items-center justify-between">
            <!-- Left side filters -->
            <div class="flex items-center gap-4">
                <ButtonPopover
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Program type
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedProgramTypes.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedProgramTypes.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li
                                v-for="option in programTypeOptions"
                                :key="option.id"
                            >
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        toggleProgramTypeSelection(option)
                                    "
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="
                                            isProgramTypeSelected(option)
                                        "
                                        @click.stop
                                        @change="
                                            toggleProgramTypeSelection(option)
                                        "
                                    />
                                    <span>{{ option.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedProgramTypes.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="resetProgramTypeFilter()"
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>

                <ButtonPopover
                    v-if="isSingleUser"
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                            Added to My Points
                        </span>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 text-slate-600 dark:text-slate-300"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <div class="flex p-2 items-center gap-6 rounded">
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm leading-5 font-normal whitespace-nowrap"
                            >
                                Only programs added to My Points
                            </span>
                            <Switch
                                :value="singleUserToggle"
                                label="Only programs added to My Points"
                                @input="$emit('update:singleUserToggle', $event)"
                            />
                        </div>
                    </template>
                </ButtonPopover>

                <!-- Multi-User Checkbox -->
                <ButtonPopover
                    v-else
                    buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
                >
                    <template #button-content>
                        <span class="text-primary text-sm font-medium">
                            Added to My Points
                        </span>
                        <Transition name="fade">
                            <span
                                v-if="selectedAddedToMyPoints.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedAddedToMyPoints.length }}
                            </span>
                        </Transition>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 icons-primary"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li
                                v-for="option in addedToMyPointsOptions"
                                :key="option.id"
                            >
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="
                                        toggleAddedToMyPointsSelection(option)
                                    "
                                >
                                    <input
                                        type="checkbox"
                                        class="rounded text-10x-orange"
                                        :checked="
                                            isAddedToMyPointsSelected(option)
                                        "
                                        @click.stop
                                        @change="
                                            toggleAddedToMyPointsSelection(
                                                option
                                            )
                                        "
                                    />
                                    <span>{{ option.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedAddedToMyPoints.length >= 1">
                                <button
                                    type="button"
                                    class="w-full p-2 text-sm text-gray-900 dark:text-gray-100 rounded text-left hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none transition-colors flex items-center gap-2"
                                    @click="resetAddedToMyPointsFilter()"
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>

                <!-- Reset all button -->
                <button
                    v-if="hasFilterSelections"
                    class="btn-base btn-transparent !pl-4 !pr-3 !py-1.5"
                    @click="clearAllFilters()"
                >
                    <span class="text-primary text-sm font-medium">
                        Reset all
                    </span>
                    <Icon icon="x" class="w-3 h-3 icons-primary" />
                </button>
            </div>

            <!-- Right side: Sort/Group (favorites only) -->
            <div
                v-if="filters.favorites && paginatedPrograms.length > 0"
                class="flex items-center gap-3"
            >
                <ButtonPopover
                    buttonClass="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                    <template #button-content>
                        <Icon
                            icon="groups"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100"
                        />
                        <span>{{ groupButtonText }}</span>
                        <Icon
                            icon="chevron-down"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <div class="flex flex-col items-start flex-1">
                            <div
                                v-for="option in groupOptions"
                                :key="option.id"
                                class="flex p-2 items-center gap-2 flex-1 w-full rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                @click="selectGroupOption(option, closePopover)"
                            >
                                <span
                                    class="flex-1 text-left text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                                >
                                    {{ option.name }}
                                </span>
                            </div>
                        </div>
                    </template>
                </ButtonPopover>

                <ButtonPopover
                    buttonClass="flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                    <template #button-content>
                        <Icon
                            icon="sort"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100"
                        />
                        <span>{{ sortButtonText }}</span>
                        <Icon
                            icon="chevron-down"
                            class="w-4 h-4 text-slate-800 dark:text-slate-100"
                        />
                    </template>
                    <template #default="{ closePopover }">
                        <div class="flex flex-col items-start flex-1">
                            <div
                                v-for="option in sortOptions"
                                :key="option.id"
                                class="flex p-2 items-center gap-2 flex-1 w-full rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                @click="selectSortOption(option, closePopover)"
                            >
                                <span
                                    class="flex-1 text-left text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                                >
                                    {{ option.name }}
                                </span>
                            </div>
                        </div>
                    </template>
                </ButtonPopover>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Switch from '@/components/ui/switch/switch.vue';

defineProps({
    filters: { type: Object, required: true },
    searchQuery: { type: String, default: '' },
    selectedProgramTypes: { type: Array, default: () => [] },
    selectedAddedToMyPoints: { type: Array, default: () => [] },
    singleUserToggle: { type: Boolean, default: false },
    isSingleUser: { type: Boolean, default: false },
    programTypeOptions: { type: Array, default: () => [] },
    addedToMyPointsOptions: { type: Array, default: () => [] },
    hasFilterSelections: { type: Boolean, default: false },
    activeFiltersCount: { type: Number, default: 0 },
    paginatedPrograms: { type: Array, default: () => [] },
    // Sort / Group
    sortOptions: { type: Array, default: () => [] },
    sortButtonText: { type: String, default: 'Sort' },
    groupOptions: { type: Array, default: () => [] },
    groupButtonText: { type: String, default: 'Group' },
    showSortOverlay: { type: Boolean, default: false },
    showGroupOverlay: { type: Boolean, default: false },
    tempSelectedSort: { type: Object, default: () => ({}) },
    tempSelectedGroup: { type: Object, default: () => ({}) },
    // Methods
    isProgramTypeSelected: { type: Function, required: true },
    isAddedToMyPointsSelected: { type: Function, required: true },
    toggleProgramTypeSelection: { type: Function, required: true },
    toggleAddedToMyPointsSelection: { type: Function, required: true },
    resetProgramTypeFilter: { type: Function, required: true },
    resetAddedToMyPointsFilter: { type: Function, required: true },
    clearAllFilters: { type: Function, required: true },
    clearSearch: { type: Function, required: true },
    updateFilters: { type: Function, required: true },
    selectSortOption: { type: Function, required: true },
    selectGroupOption: { type: Function, required: true },
    openSortOverlay: { type: Function, required: true },
    closeSortOverlay: { type: Function, required: true },
    applySortOverlay: { type: Function, required: true },
    openGroupOverlay: { type: Function, required: true },
    closeGroupOverlay: { type: Function, required: true },
    applyGroupOverlay: { type: Function, required: true },
});

defineEmits([
    'update:searchQuery',
    'update:singleUserToggle',
    'update:tempSelectedSort',
    'update:tempSelectedGroup',
    'open-mobile-filters',
]);
</script>
